import { useEffect, useState } from 'react';
import { api, handleNetworkError } from '../../utils/api';
import useInterval from '../../hooks/useInterval';
import useCurrentUser from '../../hooks/useCurrentUser';

import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from '../Button/Button';
import PasswordInput from "../PasswordInput/PasswordInput";

import { Trans as T } from 'react-i18next';
import './OTPModal.scss';

export default function OTPModal({ title, show, children, cancelText, inputConfirm, confirmText, onHide, onConfirm, message, ...props }) {
  const { currentUser } = useCurrentUser();
  
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState(null);
  const [validated, setValidated] = useState(false);
  const [codeTime, setCodeTime] = useState(0);

  useEffect(() => {
    if (show) {
      setPassword('');
      setCode('');
      setShowOTP(false);
      setProcessing(false);
      setErrors(null);
    }
  }, [show]);

  useInterval(() => {
    codeTime > 0 && setCodeTime(codeTime - 1);
  }, 1000);

  const onConfirmForm = async e => {
    e.preventDefault();

    setProcessing(true);
    setErrors(null);
    setValidated(false)

    // validate password
    if (!showOTP) {
      try {
        await api.post('/auth/verify/', {
          method: "password",
          password,
        });

        onConfirm();
      }

      catch (e) {
        console.log('e:', e);
        handleNetworkError({ e, setErrors, setValidated });
        setProcessing(false);
      }
    }

    // validate OTP
    else {
      try {
        await api.post('/auth/verify/', {
          method: "otp",
          code,
        });

        onConfirm();
      }

      catch (e) {
        console.log('e:', e);
        handleNetworkError({ e, setErrors, setValidated });
        setProcessing(false);
      }
    }
  }

  const sendCode = async () => {
    // a code was sent recently, use it:
    if (codeTime !== 0) {
      setCode('');
      setShowOTP(true);
      return;
    }

    setProcessing(true);
    setErrors(null);
    setValidated(false);

    try {
      await api.post('/auth/verify/otp/');
      setShowOTP(true);
      setCode('');
      setCodeTime(60);
    }
    catch (e) {
      handleNetworkError({ e, setErrors, setValidated });
      console.log('e:', e);
    }
    finally {
      setProcessing(false);
    }
  }

  const usePassword = () => {
    setShowOTP(false);
    setPassword('');
  }

  const onChangeCode = e => {
    setCode(e.target.value.replace(/\D+/g, '').slice(0, 6));
  }

  const renderPassword = () => (
    <>
      { message || <p>For security purposes, this action requires you to confirm your password:</p> }
      <PasswordInput minLength={6} required value={password} onChange={e => setPassword(e.target.value)} isValid={validated && !errors?.password} isInvalid={errors?.password} feedback={ errors?.password?.map(error => <Form.Control.Feedback key={error} type="invalid"><T>{ error }</T></Form.Control.Feedback> )} />
      <Button variant='link' className='send-otp to-email' type='button' onClick={sendCode}>Send code via email</Button>

      { errors?.detail?.length && (
        errors.detail.map(error => <div className='error-details'>{ error }</div>)
      )}
    </>
  );

  const renderOTP = () => (
    <>
      <p>Enter the six-digit code that you received to the email address <strong>{currentUser.email}</strong>:</p>
      <Form.Control autoFocus type='text' autoComplete='off' value={code} className='otp-input' onChange={onChangeCode}  isValid={validated && !errors?.code} isInvalid={errors?.code} />
      { errors?.code?.map(error => <Form.Control.Feedback key={error} type="invalid" className='otp-error'>{ error }</Form.Control.Feedback> )}

      <Button variant='link' className='send-otp' onClick={usePassword} type='button'>Verify with your password</Button>

      { codeTime > 0
      ? <p className='resend-timer'>Resend code in {codeTime} seconds</p>
      : <Button variant='link' type='button' className='send-otp' onClick={sendCode} disabled={processing}>Resend code</Button>
      }
    </>
  );

  return (
    <Modal className="__modal __otp-modal" onHide={onHide} show={show} {...props} backdrop='static' centered>
      <Modal.Header onHide={onHide} closeButton><h2>Password required</h2></Modal.Header>
      <Form onSubmit={onConfirmForm} noValidate validated={validated && !errors}>
        <Modal.Body>
          { showOTP ? renderOTP() : renderPassword() }
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' className='cancel' onClick={onHide}>{ cancelText || 'Cancel' }</Button>
          <Button type='submit' className='confirm' loading={processing}>{ confirmText || 'Confirm' }</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
